import { Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import greenv from "../../../green-v-md.png";
import { SpaceApartDiv } from "../../restaurantBuilderLegacy/RestaurantCreateBuild";

/**
 * Small marker dot for showing other restaurants on the map.
 */
const ContextMarkerDot = ({ name }) => (
  <div title={name} style={{
    width: 10, height: 10,
    background: '#2e7d32',
    border: '2px solid white',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    transform: 'translate(-50%, -50%)',
  }} />
);

const LocationPickerWidget = ({
  isDisabled,
  name,
  setFormData,
  unsetGeoJson,
  submitCallback,
  defaultZoom,
  submitButtonText,
  contextMarkers,  // [{lat, lng, name}] — other restaurants to show
  overlayConfig,   // {imageUrl, bounds: {south,west,north,east}, opacity}
  heading,         // Event heading (degrees) for map rotation
  ...props
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const map = useMap("location-picker-map");
  const overlayRef = useRef(null);

  const areCoordinatesValid =
    unsetGeoJson &&
    unsetGeoJson.coordinates &&
    Array.isArray(unsetGeoJson.coordinates) &&
    unsetGeoJson.coordinates.length >= 2 &&
    typeof unsetGeoJson.coordinates[0] === "number" &&
    typeof unsetGeoJson.coordinates[1] === "number" &&
    !isNaN(unsetGeoJson.coordinates[0]) &&
    !isNaN(unsetGeoJson.coordinates[1]);

  const buttonDisabled = isDisabled || !mapLoaded || !areCoordinatesValid;

  // Track map ready state
  useEffect(function trackMapReady() {
    if (!map) { setMapLoaded(false); return; }
    const listener = map.addListener("idle", () => setMapLoaded(true));
    return () => listener.remove();
  }, [map]);

  // Apply heading when it changes
  useEffect(function syncHeading() {
    if (!map || heading === undefined) return;
    try { map.setHeading(heading || 0); } catch (e) {}
  }, [map, heading]);

  // Render the event overlay when the map is ready and overlay config is provided
  useEffect(function renderOverlay() {
    if (!map || !window.google?.maps || !overlayConfig) return;
    const { imageUrl, bounds, opacity } = overlayConfig;
    if (!imageUrl || !bounds) return;

    // Clean up previous overlay
    if (overlayRef.current) {
      try { overlayRef.current.setMap(null); } catch (e) {}
      overlayRef.current = null;
    }

    try {
      const mapBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(bounds.south, bounds.west),
        new window.google.maps.LatLng(bounds.north, bounds.east)
      );
      overlayRef.current = new window.google.maps.GroundOverlay(
        imageUrl, mapBounds, {
          opacity: opacity || 0.75,
          clickable: false,
          map: map,
        }
      );
    } catch (e) {
      console.debug("Could not render overlay:", e.message);
    }

    return () => {
      if (overlayRef.current) {
        try { overlayRef.current.setMap(null); } catch (e) {}
        overlayRef.current = null;
      }
    };
  }, [map, mapLoaded, overlayConfig]);

  return (
    <Fragment>
      <SpaceApartDiv className="map-square-relative">
        <Map
          id="location-picker-map"
          center={{
            lat: areCoordinatesValid ? unsetGeoJson.coordinates[1] : 0,
            lng: areCoordinatesValid ? unsetGeoJson.coordinates[0] : 0,
          }}
          defaultZoom={defaultZoom || 12}
          gestureHandling="greedy"
          mapTypeControl={false}
          streetViewControl={false}
          mapId="ae90486c215209d65677867a"
          heading={heading || 0}
          onCameraChanged={(ev) => {
            const { center } = ev.detail;
            setFormData((old) => ({
              ...old,
              unsetGeoJson: {
                coordinates: [center.lng, center.lat],
              },
            }));
          }}
        >
          {/* Context markers: other restaurants in the event */}
          {contextMarkers && contextMarkers.map((marker, idx) => (
            <AdvancedMarker
              key={idx}
              position={{ lat: marker.lat, lng: marker.lng }}
            >
              <ContextMarkerDot name={marker.name} />
            </AdvancedMarker>
          ))}
        </Map>
        <div>
          <img
            title={name}
            name={name}
            src={greenv}
            alt=""
            width="32"
            className="map-marker-center"
          />
          <div className="map-marker-tag map-marker-tag-position">
            {name?.length >= 16 ? name.slice(0, 14) + "..." : name}
          </div>
        </div>
      </SpaceApartDiv>
      <input
        className={`btn-primary btn-center ${isDisabled ? "loading" : ""}`}
        disabled={buttonDisabled}
        onClick={submitCallback}
        type="button"
        value={submitButtonText || "Get Address for Location"}
      />
    </Fragment>
  );
};
export default connect((state) => {}, {})(LocationPickerWidget);
